/* eslint-disable react/prop-types */
import React, { useState, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Box,
  Collapse,
  IconButton,
  Typography,
  Tooltip,
  makeStyles,
  Grid,
  useMediaQuery,
} from "@material-ui/core";
import ScaleLoader from "react-spinners/ScaleLoader";
import {
  computeEstimationTotals,
  groupEstimationsByDate,
} from "lib/utils/estimations";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@material-ui/icons/KeyboardArrowUp";
import LockIcon from "@material-ui/icons/Lock";
import athenaClient from "lib/axios/clients";
import { groupByPriceAndReduceEstimations } from "lib/filters/estimations";
import {
  REACT_APP_BASE_URL,
  primaryColor,
  PriceOut,
} from "resources/constants";
import buildRequestUrl from "lib/requests/requestUtils";
import {
  filterEstimationDateFieldsWithoutIdentifier,
  getEstimations,
} from "lib/requests/estimations";
import getElasticities from "lib/requests/elasticities";
import useDidMountEffect, { useEffectAsync } from "lib/react/customHooks";
import _ from "lodash";
import {
  setScenarioEstimations,
  setSelectedScenario,
  setCacheReferenceScenario,
  setCacheScenario,
  setReferenceScenario,
  addScenarioTotals,
} from "app/scenarios/duck/slices";
import { handleScenarioType } from "lib/utils/scenarios";
import CardComponent from "app/common/CardComponent";
import mixpanelTrack from "app/common/MixpanelComponent";
import moment from "moment";
import { BsArrowUp } from "react-icons/bs";
import { getFormattedCellValue, getNestedObject, nFormatter } from "lib/utils";
import BadRequestDialog from "app/common/dialog/BadRequestDialog";
import ElasticityCurveChartComponent from "./ElasticityCurveChartComponent";
import SelectorCycleTradePromotionAnnualComponent from "./SelectorCycleTradePromotionAnnualComponent";
import HistogramChartComponent from "./HistogramChartComponent";

const useRowStyles = makeStyles((theme) => ({
  row: {
    display: "flex",
    justifyContent: "space-evenly",
    backgroundColor: "#fcfcfc",
    "&:hover": {
      cursor: "pointer",
      backgroundColor: "#ECEFF1",
    },
  },
  rowCell: {
    padding: "10px 5px 10px 5px",
    display: "flex",
    justifyContent: "center",
    flexDirection: "column",
    height: "60px",
    "&:first-child": {
      flexBasis: "50px",
      maxWidth: "50px",
      minWidth: "50px",
    },
    width: "145px",
    minWidth: "145px",
  },
  large: {
    wordBreak: "break-all",
    width: "145px",
    minWidth: "145px",
  },
  cellValue: {
    display: "-webkit-box",
    overflow: "hidden",
    textOverflow: "ellipsis",
    "-webkit-line-clamp": "2",
    "-webkit-box-orient": "vertical",
    [theme.breakpoints.up("xs")]: {
      fontSize: "0.750rem",
    },
    [theme.breakpoints.up("sm")]: {
      fontSize: "0.750rem",
    },
    [theme.breakpoints.up("md")]: {
      fontSize: "0.750rem",
    },
    [theme.breakpoints.up("lg")]: {
      fontSize: "0.800rem",
    },
    [theme.breakpoints.up("xl")]: {
      fontSize: "0.800rem",
    },
  },
  numeric: {
    alignItems: "flex-end",
  },
  expansionDetailsContainer: {
    // Height of the row render in EstimationComponent
    height: "360px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    // padding: theme.spacing(2),
  },
  customCardStyle: {
    position: "relative",
  },
  customLoadingContainer: {
    width: "97%",
    position: "absolute",
    display: "grid",
    height: "100%",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#0000002b",
  },
  light: {
    color: "#9e9e9e",
  },
  cellPercentage: {
    marginLeft: "5px",
    display: "flex",
    alignItems: "center",
    fontSize: "12px",
  },
  darkGreyBackground: {
    backgroundColor: "rgba(221, 221, 221, 0.2)",
  },
  expandButton: {
    "&:hover": {
      backgroundColor: "transparent",
    },
  },
  itemContainer: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    width: "100%",
  },
  textSlider: {
    alignSelf: "center",
    fontWeight: "bold",
    fontSize: 18,
    color: "#999999",
    display: "flex",
    justifyContent: "center",
  },
  textCardContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    marginBottom: "5px",
    color: "#999999",
  },
  textAvgPriceContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    marginBottom: "28px",
    marginLeft: "170px",
    paddingRight: "20px",
    color: "#666666",
  },
  textAvgPriceContainerSix: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    marginBottom: "28px",
    marginLeft: "410px",
    paddingRight: "20px",
    color: "#666666",
  },
  textAvgPriceContainerThree: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    marginBottom: "28px",
    marginLeft: "555px",
    paddingRight: "20px",
    color: "#666666",
  },
  textAvgPriceContainerWithScroll: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    marginBottom: "28px",
    marginLeft: "450px",
    paddingRight: "20px",
    color: "#666666",
  },
  textAvgPriceContainerWithScrollSix: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    marginBottom: "28px",
    marginLeft: "750px",
    paddingRight: "20px",
    color: "#666666",
  },
  textAvgPriceContainerWithScrollThree: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    marginBottom: "28px",
    marginLeft: "890px",
    paddingRight: "20px",
    color: "#666666",
  },
  textAvgPriceContainerSpecificRange: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    marginBottom: "28px",
    marginLeft: "170px",
    paddingRight: "20px",
    color: "#666666",
  },
  textAvgPriceContainerMedium: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    marginBottom: "28px",
    marginLeft: "260px",
    paddingRight: "20px",
    color: "#666666",
  },
  textAvgPriceContainerMediumSix: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    marginBottom: "28px",
    marginLeft: "515px",
    paddingRight: "20px",
    color: "#666666",
  },
  textAvgPriceContainerMediumThree: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    marginBottom: "28px",
    marginLeft: "650px",
    paddingRight: "20px",
    color: "#666666",
  },
  textAvgPriceContainerSpecific: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    marginBottom: "28px",
    marginLeft: "230px",
    paddingRight: "20px",
    color: "#666666",
  },
  textAvgPriceContainerSpecificSix: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    marginBottom: "28px",
    marginLeft: "460px",
    paddingRight: "20px",
    color: "#666666",
  },
  textAvgPriceContainerSpecificThree: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    marginBottom: "28px",
    marginLeft: "610px",
    paddingRight: "20px",
    color: "#666666",
  },
  textAvgPriceContainerSmall: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    marginBottom: "28px",
    marginLeft: "170px",
    paddingRight: "20px",
    color: "#666666",
  },
  textAvgPriceContainerSmallSix: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    marginBottom: "28px",
    marginLeft: "390px",
    paddingRight: "20px",
    color: "#666666",
  },
  textAvgPriceContainerSmallThree: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    marginBottom: "28px",
    marginLeft: "545px",
    paddingRight: "20px",
    color: "#666666",
  },
  textDescriptionHistogram: {
    marginBottom: "20px",
    fontSize: 12,
  },
  textSmallSize: {
    fontSize: 12,
    marginBottom: "17px",
  },
  textDescriptionHistogramContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    color: "#999999",
    paddingLeft: "15px",
  },
  textOwnElasticity: {
    fontWeight: "bold",
  },
  titleCardContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    paddingTop: "15px",
  },
  titleCard: {
    fontWeight: "bold",
    fontSize: 22,
    color: "#666666",
  },
  selectorCycleContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    paddingTop: "20px",
    marginBottom: "5px",
  },
  histogramChartContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    width: "134%",
    // marginTop: "10px",
    // marginBottom: "10px",
  },
  histogramChartContainerSix: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    width: "74%",
  },
  histogramChartContainerThree: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    width: "65%",
  },
  histogramChartContainerWithScroll: {
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-end",
    width: "98%",
  },
  histogramChartContainerWithScrollSix: {
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-end",
    width: "67%",
  },
  histogramChartContainerWithScrollThree: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    width: "60%",
  },
  histogramChartContainerMedium: {
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-end",
    width: "115%",
  },
  histogramChartContainerMediumSix: {
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-end",
    width: "71%",
  },
  histogramChartContainerMediumThree: {
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-end",
    width: "61%",
  },
  histogramChartContainerSmall: {
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-end",
    width: "140%",
  },
  histogramChartContainerSmallSix: {
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-end",
    width: "75%",
  },
  histogramChartContainerSmallThree: {
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-end",
    width: "65%",
  },
  histogramChartContainerSpecific: {
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-end",
    width: "128%",
  },
  histogramChartContainerSpecificSix: {
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-end",
    width: "73%",
  },
  histogramChartContainerSpecificThree: {
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-end",
    width: "64%",
  },
  chartContainer: {
    position: "relative",
    [theme.breakpoints.up("xs")]: {
      overflow: "hidden",
      flexGrow: "1",
      // height: "150px",
    },
  },
  textAvgPriceNumber: {
    alignSelf: "center",
  },
}));

export default function EstimationRowTradePromotionAnnualComponent({
  row,
  referenceRow,
  isReference,
  style,
  index,
  groupBy,
  setSelectedIndex,
  expandedRow,
  setExpandedRow,
  isLoading,
  setIsLoading,
  isBlocked,
  // isLoadingPriceChange,
  // setIsLoadingPriceChange,
}) {
  const labelTableColumns = ["product", "retailer", "area"];
  const numericTableColumns = [
    "price",
    "units",
    "sales",
    "margin",
    "units_in",
    "sales_in",
  ];
  const isTradePromotionAnnual = true;
  const classes = useRowStyles();
  // const rowContainer = React.useRef(null);
  const dispatch = useDispatch();
  const [currentTupleData, setCurrentTupleData] = useState(null);
  const [selectedPrice, setSelectedPrice] = useState(referenceRow.price);
  const [currentRowPriceByDate, setCurrentRowPriceByDate] = useState({});
  const catalogSlice = useSelector((state) => state.catalog);
  const retailerSlice = useSelector((state) => state.retailers);
  const areaSlice = useSelector((state) => state.areas);
  const scenariosSlice = useSelector((state) => state.scenarios);
  const metadataSlice = useSelector((state) => state.metadata);
  const configCatSlice = useSelector((state) => state.auth.configCatUser);
  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const isMediumScreen = useMediaQuery((theme) => theme.breakpoints.up("1900"));
  const isTwoThousandScreen = useMediaQuery((theme) =>
    theme.breakpoints.down("1951")
  );
  const isRangeDownScreen = useMediaQuery((theme) =>
    theme.breakpoints.down("1537")
  );
  const isRangeUpScreen = useMediaQuery((theme) =>
    theme.breakpoints.up("1300")
  );
  const isLargeScreen = useMediaQuery((theme) => theme.breakpoints.up("2400"));
  // This variable will use to avoid doing extra logic when we need to change the data in a monthly level because we just need to change the price selected without doing any logic.
  const [selectedRowByDate, setSelectedRowByDate] = useState(null);
  const [selectedReferenceRowByDate, setSelectedReferenceRowByDate] = useState(
    null
  );
  const [ownElasticity, setOwnElasticity] = useState(null);
  const [isMonthLevelChange, setIsMonthLevelChange] = useState(null);
  const [currentMonth, setCurrentMonth] = useState("AllMonths");
  const [isDataReady, setIsDataReady] = useState(false);
  const [titleCycleActive, setTitleCycleActive] = useState(
    `From ${moment(
      scenariosSlice.selectedScenario.scenario.cycle.initial_date
    ).format("MMM'YY")} to ${moment(
      scenariosSlice.selectedScenario.scenario.cycle.final_date
    ).format("MMM'YY")}`
  );
  const [isBadRequest, setIsBadRequest] = useState(false);
  const [isLoadingPriceChange, setIsLoadingPriceChange] = useState(false);

  const diffbetweenDates = Math.round(
    moment(scenariosSlice.selectedScenario.scenario.cycle.final_date).diff(
      moment(scenariosSlice.selectedScenario.scenario.cycle.initial_date),
      "months",
      true
    ) - 1
  );

  const listCycles = [
    "AllMonths",
    moment(scenariosSlice.selectedScenario.scenario.cycle.initial_date).format(
      "MMM'YY"
    ),
  ];
  for (let i = 1; i <= diffbetweenDates; i += 1) {
    if (i === diffbetweenDates) {
      listCycles.push(
        moment(
          scenariosSlice.selectedScenario.scenario.cycle.final_date
        ).format("MMM'YY")
      );
    } else {
      listCycles.push(
        moment(scenariosSlice.selectedScenario.scenario.cycle.initial_date)
          .add(i, "M")
          .format("MMM'YY")
      );
    }
  }

  // We use this variable to avoid having to use the ternary expression inside useState everywhere we want to paint rowValues. To avoid extra requests to the server, every time an estimation changes we will update the value of selected estimation so we do not have to ask the server for the new value after the update
  const [rowValues, setRowValues] = useState(row);

  const priceOutLabelByTenant = configCatSlice?.features.isUsaTerminology
    ? PriceOut
    : "Price Out";

  useDidMountEffect(() => {
    const asyncFunction = async () => {
      try {
        // setIsLoading(true);
        setIsLoadingPriceChange(true);
        let estimationsForPrice;
        if (!isMonthLevelChange) {
          if (!selectedRowByDate) {
            estimationsForPrice = await getEstimations({
              filters: {
                product: row.product,
                ...(row.retailer && { retailer: row.retailer }),
                ...(row.retailer_group && {
                  retailer_group: row.retailer_group,
                }),
                ...(row.area && { area: row.area }),
                min_date:
                  scenariosSlice.selectedScenario.scenario.cycle.initial_date,
                max_date:
                  scenariosSlice.selectedScenario.scenario.cycle.final_date,
                min_price: selectedPrice - 0.001,
                max_price: selectedPrice + 0.001,
                data_source_id: 1,
              },
            });
            const currentRowPriceByMonth = {};
            _.forEach(
              _.sortBy(estimationsForPrice.data, ["date"]),
              (estimations) => {
                currentRowPriceByMonth[estimations.date] =
                  estimationsForPrice.data[0].price;
              }
            );

            setCurrentRowPriceByDate(currentRowPriceByMonth);
          } else {
            estimationsForPrice = await getEstimations({
              filters: {
                product: row.product,
                ...(row.retailer && { retailer: row.retailer }),
                ...(row.area && { area: row.area }),
                date: currentMonth,
                min_price: selectedPrice - 0.001,
                max_price: selectedPrice + 0.001,
                data_source_id: 1,
              },
            });
            if (groupBy) {
              const groupedByPriceEstimation = groupByPriceAndReduceEstimations(
                estimationsForPrice.data,
                metadataSlice["custom-projection"],
                ""
              );
              setCurrentRowPriceByDate({
                ...currentRowPriceByDate,
                ...{ [currentMonth]: groupedByPriceEstimation[0].price },
              });
            } else {
              setCurrentRowPriceByDate({
                ...currentRowPriceByDate,
                ...{ [currentMonth]: estimationsForPrice.data[0].price },
              });
            }
          }

          const updatedScenarioEstimations = {};

          // For each tuple involved in the price change we want to find the current scenario estimation for that tuple and update it with the values for the new price
          const scenarioEstimationInEstimationByDate = [];
          _.forEach(estimationsForPrice.data, (estimation) => {
            const estimationKey = `${estimation.product_id}-${estimation.area_id}-${estimation.retailer_id}-${estimation.date}`;
            if (
              scenariosSlice.selectedScenario.estimationsByDate[
                estimationKey
              ] !== undefined
            ) {
              scenarioEstimationInEstimationByDate.push(estimation);
            }
          });
          const scenarioEstimationsToUpdate = _.map(
            scenarioEstimationInEstimationByDate,
            (estimation) => {
              const estimationKey = `${estimation.product_id}-${estimation.area_id}-${estimation.retailer_id}-${estimation.date}`;
              const targetScenarioEstimation =
                scenariosSlice.selectedScenario.estimationsByDate[
                  estimationKey
                ];

              const newEstimationValues = filterEstimationDateFieldsWithoutIdentifier(
                estimation
              );

              // The reason why we are creating an object in addition to the array returned by the map is that the estimations stored in the object contain expanded properties that are used by the client application while the array resulting from the map have the bare minimum properties required to perform updates in the server side
              updatedScenarioEstimations[estimationKey] = {
                ...targetScenarioEstimation,
                ...newEstimationValues,
              };

              return {
                id: targetScenarioEstimation.id,
                scenario_id: targetScenarioEstimation.scenario_id,
                estimation_id: estimation.id,
                ...newEstimationValues,
              };
            }
          );

          // Create a subset of unique triplets (product,retailer,area)
          const EstimationsUpdated = {
            estimationsByDate: {
              ...scenariosSlice.selectedScenario.estimationsByDate,
              ...updatedScenarioEstimations,
            },
          };
          const groupedValuesByTriplet = _.groupBy(
            EstimationsUpdated.estimationsByDate,
            (estimation) => {
              const key = `${estimation.product_id}-${estimation.area_id}-${estimation.retailer_id}`;
              return key;
            }
          );
          const estimationsByCycleUpdated = groupEstimationsByDate(
            groupedValuesByTriplet,
            metadataSlice["custom-projection"],
            configCatSlice?.features.newInvestmentCalculation
          );
          let rowValueUpdate;
          const keyToUpdate = `${rowValues.product_id}${
            row.area ? "-".concat(rowValues.area_id) : ""
          }${row.retailer ? "-".concat(rowValues.retailer_id) : ""}${
            row.retailer_group ? "-".concat(rowValues.retailer_group_id) : ""
          }`;

          if (groupBy) {
            const groupedValues = _.groupBy(
              EstimationsUpdated.estimationsByDate,
              (estimation) => {
                const key = `${estimation.product_id}${
                  row.area ? "-".concat(estimation.area_id) : ""
                }${row.retailer ? "-".concat(estimation.retailer_id) : ""}${
                  row.retailer_group
                    ? "-".concat(estimation.retailer.retailer_group_id)
                    : ""
                }`;
                return key;
              }
            );
            rowValueUpdate = groupEstimationsByDate(
              groupedValues,
              metadataSlice["custom-projection"],
              configCatSlice?.features.newInvestmentCalculation
            )[keyToUpdate];
          } else rowValueUpdate = estimationsByCycleUpdated[keyToUpdate];

          let currentEstimationWithAdjust;
          if (selectedRowByDate) {
            const keyMonthlyToUpdate = `${rowValues.product_id}${
              row.area ? "-".concat(rowValues.area_id) : ""
            }${row.retailer ? "-".concat(rowValues.retailer_id) : ""}${
              row.retailer_group ? "-".concat(rowValues.retailer_group_id) : ""
            }-${currentMonth}`;
            currentEstimationWithAdjust =
              updatedScenarioEstimations[keyMonthlyToUpdate];

            if (groupBy) {
              const groupedValues = _.groupBy(
                EstimationsUpdated.estimationsByDate,
                (estimation) => {
                  const key = `${estimation.product_id}${
                    row.area ? "-".concat(estimation.area_id) : ""
                  }${row.retailer ? "-".concat(estimation.retailer_id) : ""}${
                    row.retailer_group
                      ? "-".concat(estimation.retailer.retailer_group_id)
                      : ""
                  }-${estimation.date}`;
                  return key;
                }
              );
              currentEstimationWithAdjust = groupEstimationsByDate(
                groupedValues,
                metadataSlice["custom-projection"],
                configCatSlice?.features.newInvestmentCalculation
              )[keyMonthlyToUpdate];
            }
            setSelectedRowByDate(currentEstimationWithAdjust);
          } else {
            currentEstimationWithAdjust = rowValueUpdate;
          }

          setRowValues({
            ...rowValues,
            price: rowValueUpdate.price,
            price_in: rowValueUpdate.price_in,
            units: rowValueUpdate.units,
            units_in: rowValueUpdate.units_in,
            sales: rowValueUpdate.sales,
            sales_in: rowValueUpdate.sales_in,
            margin: rowValueUpdate.margin,
          });

          // Reduce Estimations Affected
          EstimationsUpdated.estimations = {
            ...scenariosSlice.selectedScenario.estimations,
            ...estimationsByCycleUpdated,
          };

          dispatch(setSelectedScenario(EstimationsUpdated));

          dispatch(
            setCacheScenario({
              [handleScenarioType(
                scenariosSlice.selectedScenario.scenario.cycle.cycle_type_id
              )]: {
                ...scenariosSlice.selectedScenario,
                estimations: EstimationsUpdated.estimations,
                estimationsByDate: {},
              },
            })
          );

          if (
            scenariosSlice.selectedScenario.scenario.id ===
            scenariosSlice.referenceScenario.scenario.id
          ) {
            const EstimationsReferenceUpdated = {
              estimations: {
                ...scenariosSlice.referenceScenario.estimations,
                ...EstimationsUpdated.estimations,
              },
              estimationsByDate: {
                ...scenariosSlice.referenceScenario.estimationsByDate,
                ...EstimationsUpdated.estimationsByDate,
              },
            };
            dispatch(setReferenceScenario(EstimationsReferenceUpdated));
            dispatch(
              setCacheReferenceScenario({
                [handleScenarioType(
                  scenariosSlice.referenceScenario.scenario.cycle.cycle_type_id
                )]: {
                  ...scenariosSlice.referenceScenario,
                  estimations: EstimationsUpdated.estimations,
                  estimationsByDate: {},
                },
              })
            );
          }

          // We update the scenario totals
          const newTotals = {
            [scenariosSlice.selectedScenario.scenario.id]: {
              ...scenariosSlice.selectedScenario,
              ...computeEstimationTotals(
                {
                  ...scenariosSlice.selectedScenario.estimations,
                  ...estimationsByCycleUpdated,
                },
                scenariosSlice.referenceScenario.estimations,
                configCatSlice?.features.newInvestmentCalculation,
                configCatSlice?.features.roiStrategy
              ),
              estimationsByDate: {},
            },
          };

          dispatch(addScenarioTotals(newTotals));

          dispatch(
            setScenarioEstimations({
              id: scenariosSlice.selectedScenario.scenario.id,
              estimations: EstimationsUpdated.estimations,
              estimationsByDate: {},
            })
          );

          // Finally we request the server to update the scenario estimations in the database
          const config = {
            method: "PATCH",
            url: `${REACT_APP_BASE_URL}/scenario_estimations_by_date`,
            data: scenarioEstimationsToUpdate,
          };

          await athenaClient(config);
        }
      } catch (error) {
        setIsBadRequest(true);
      } finally {
        // setIsLoading(false);
        setIsLoadingPriceChange(false);
        setIsMonthLevelChange(false);
      }
    };
    asyncFunction();
  }, [selectedPrice]);

  useMemo(() => {
    setRowValues({ ...row, isMonthPriceChange: null });
  }, [row]);

  const handleEstimationRequest = async (estimationsConfig) => {
    const estimationsResponse = await athenaClient(estimationsConfig);
    return estimationsResponse.data;
  };

  useEffectAsync(
    async () => {
      if (index === expandedRow) {
        let estimationsConfig;
        if (currentMonth !== "AllMonths") {
          const tupleRequestParams = {
            filters: {
              product: row.product,
              ...(row.retailer && { retailer: row.retailer }),
              ...(row.retailer_group && {
                retailer_group: row.retailer_group,
              }),
              ...(row.area && { area: row.area }),
              date: currentMonth,
              data_source_id: 1,
            },
          };
          const estimationsUrl = buildRequestUrl(
            {
              ...tupleRequestParams,
              group_by: ["price"],
              sum: ["units", "margin", "units_in", "sales"],
            },
            "estimations"
          );

          estimationsConfig = {
            method: "GET",
            url: estimationsUrl,
          };
        } else {
          const tupleRequestParams = {
            filters: {
              product: row.product,
              ...(row.retailer && { retailer: row.retailer }),
              ...(row.retailer_group && {
                retailer_group: row.retailer_group,
              }),
              ...(row.area && { area: row.area }),
              cycle: { id: scenariosSlice.selectedScenario.scenario.cycle_id },
            },
          };
          const estimationsUrl = buildRequestUrl(
            {
              ...tupleRequestParams,
              group_by: ["price"],
              sum: ["units", "margin", "units_in", "sales"],
            },
            "estimations_by_cycle"
          );

          estimationsConfig = {
            method: "GET",
            url: estimationsUrl,
          };
        }
        return handleEstimationRequest(estimationsConfig);
      }
      return null;
    },
    (response) => {
      if (response !== null) {
        setIsMonthLevelChange(true);
        let localRowValues;
        let currentPrice;
        let localEstimations;
        if (currentMonth !== "AllMonths") {
          const currentRow = `${row.product_id}-${row.area_id}-${row.retailer_id}-${currentMonth}`;
          localRowValues =
            scenariosSlice.selectedScenario.estimationsByDate[currentRow];
          if (!isReference) {
            setSelectedReferenceRowByDate(
              scenariosSlice.referenceScenario.estimationsByDate[currentRow]
            );
          } else {
            setSelectedReferenceRowByDate(localRowValues);
          }

          setSelectedRowByDate(localRowValues);

          localEstimations = groupByPriceAndReduceEstimations(
            response,
            metadataSlice["custom-projection"],
            "sum_"
          );

          if (groupBy) {
            const keyToUpdate = `${rowValues.product_id}${
              row.area ? "-".concat(rowValues.area_id) : ""
            }${row.retailer ? "-".concat(rowValues.retailer_id) : ""}${
              row.retailer_group ? "-".concat(row.retailer_group.id) : ""
            }-${currentMonth}`;
            const groupedValues = _.groupBy(
              scenariosSlice.selectedScenario.estimationsByDate,
              (estimation) => {
                const key = `${estimation.product_id}${
                  row.area ? "-".concat(estimation.area_id) : ""
                }${row.retailer ? "-".concat(estimation.retailer_id) : ""}${
                  row.retailer_group
                    ? "-".concat(estimation.retailer.retailer_group_id)
                    : ""
                }-${estimation.date}`;
                return key;
              }
            );
            const rowValueUpdate = groupEstimationsByDate(
              groupedValues,
              metadataSlice["custom-projection"],
              configCatSlice?.features.newInvestmentCalculation
            )[keyToUpdate];

            const groupedReferenceValues = _.groupBy(
              scenariosSlice.referenceScenario.estimationsByDate,
              (estimation) => {
                const key = `${estimation.product_id}${
                  row.area ? "-".concat(estimation.area_id) : ""
                }${row.retailer ? "-".concat(estimation.retailer_id) : ""}${
                  row.retailer_group
                    ? "-".concat(estimation.retailer.retailer_group_id)
                    : ""
                }-${estimation.date}`;

                return key;
              }
            );
            const rowValueReferenceUpdate = groupEstimationsByDate(
              groupedReferenceValues,
              metadataSlice["custom-projection"],
              configCatSlice?.features.newInvestmentCalculation
            )[keyToUpdate];

            localRowValues = rowValueUpdate;
            setSelectedRowByDate(rowValueUpdate);
            setSelectedReferenceRowByDate(rowValueReferenceUpdate);

            currentPrice = rowValueUpdate.price;
          } else {
            currentPrice =
              scenariosSlice.selectedScenario.estimationsByDate[currentRow]
                .price;
          }

          getElasticities({
            product_id: row.product_id,
            source_product_id: row.product_id,
            data_source_id: 1,
            is_product_client: 1,
            ...(row.retailer && { retailer_id: row.retailer_id }),
            ...(row.area && { area_id: row.area_id }),
            date: currentMonth,
            avg_weight_by_pred_units_last_price: ["elasticity"],
            filter_type: "same",
            group_by: "product_id",
          }).then((result) => {
            setOwnElasticity(
              _.round(
                result.data[0]?.avg_weight_by_pred_units_last_price_elasticity,
                2
              )
            );
          });
        } else {
          setSelectedReferenceRowByDate(undefined);
          setSelectedRowByDate(null);

          localEstimations = groupByPriceAndReduceEstimations(
            response,
            metadataSlice["custom-projection"],
            "sum_"
          );

          localRowValues = rowValues;
          currentPrice = rowValues.price;
          getElasticities({
            product_id: row.product_id,
            source_product_id: row.product_id,
            data_source_id: 1,
            is_product_client: 1,
            ...(row.retailer && { retailer_id: row.retailer_id }),
            ...(row.area && { area_id: row.area_id }),
            avg_weight_by_pred_units_last_price: ["elasticity"],
            group_by: "product_id",
            filter_type: "same",
            min_date:
              scenariosSlice.selectedScenario.scenario.cycle.initial_date,
            max_date: scenariosSlice.selectedScenario.scenario.cycle.final_date,
          }).then((result) => {
            setOwnElasticity(
              _.round(
                result.data[0]?.avg_weight_by_pred_units_last_price_elasticity,
                2
              )
            );
          });
        }

        let baseEstimation = _.find(localEstimations, {
          price: currentPrice,
        });
        if (baseEstimation === undefined) {
          // eslint-disable-next-line func-names
          baseEstimation = localEstimations.reduce(function(prev, curr) {
            return Math.abs(curr.price - currentPrice) <
              Math.abs(prev.price - currentPrice)
              ? curr
              : prev;
          });
        }
        const filterToApply = {
          ...(row.retailer && { retailer_id: row.retailer_id }),
          ...(row.retailer_group && {
            "retailer.retailer_group_id": row.retailer_group.id,
          }),
          ...(row.area && { area_id: row.area_id }),
          ...(currentMonth !== "AllMonths" && { date: currentMonth }),
          product_id: row.product_id,
        };
        if (groupBy) {
          const baseEstimations = _.filter(
            scenariosSlice.selectedScenario.estimationsByDate,
            (estimation) => {
              const check = [];
              // eslint-disable-next-line no-restricted-syntax
              for (const [key, value] of Object.entries(filterToApply)) {
                check.push(
                  key !== "retailer.retailer_group_id"
                    ? value === getNestedObject(estimation, key.split("."))[key]
                    : value ===
                        getNestedObject(estimation, key.split("."))[
                          key.split(".")[0]
                        ].retailer_group_id
                );
              }
              return _.every(check, Boolean);
            }
          );
          baseEstimation = _.reduce(
            baseEstimations,
            (totals, estimation) => {
              return {
                units: totals.units + estimation.units,
                margin: totals.margin + estimation.margin,
                sales: totals.sales + estimation.sales,
              };
            },
            { units: 0, margin: 0, sales: 0 }
          );
        }
        setCurrentTupleData({
          estimations: localEstimations,
        });
        // It is necessary to set the current price at the end of this function because it is provoke that the render take the old tupleRatio
        setSelectedPrice(currentPrice);
      }
    },
    [currentMonth]
  );

  const handleCurrentRowPriceByMonth = (estimationsByDate) => {
    const groupByEstimationsByDate = _.groupBy(
      _.sortBy(
        _.filter(estimationsByDate, (estimation) => {
          return (
            estimation.product_id === row.product_id &&
            (row.area ? estimation.area_id === row.area_id : true) &&
            (row.retailer
              ? estimation.retailer_id === row.retailer_id
              : true) &&
            (row.retailer_group
              ? estimation.retailer.retailer_group_id === row.retailer_group.id
              : true)
          );
        }),
        "date"
      ),
      "date"
    );
    const estimationsAvg = {};
    _.forEach(groupByEstimationsByDate, (estimation, key) => {
      estimationsAvg[key] = _.round(
        _.sumBy(estimation, (estimationAvg) => {
          return estimationAvg.price * estimationAvg.units;
        }) / _.sumBy(estimation, `units`),
        metadataSlice["custom-projection"].price[0].decimals
      );
    });
    setCurrentRowPriceByDate(estimationsAvg);
  };

  const handleOnRowClick = async () => {
    if (isBlocked) return;
    if (index === expandedRow) {
      setExpandedRow(-1);
    } else if (!isLoading) {
      try {
        setIsLoading(true);
        setSelectedReferenceRowByDate(undefined);
        setSelectedRowByDate(null);
        setIsMonthLevelChange(false);
        setCurrentTupleData(null);
        /* MIXPANEL TRACKS */
        mixpanelTrack("Expand estimation row", {
          ...(row.product && { product: row.product.label }),
          ...(row.retailer && { retailer: row.retailer.label }),
          ...(row.area && { area: row.area.label }),
          cycle: scenariosSlice.selectedScenario.scenario.cycle.label,
        });
        /* END OF MIXPANEL TRACKS */
        setSelectedIndex(index);
        setExpandedRow(index);

        const tupleRequestParams = {
          filters: {
            product: row.product,
            ...(row.retailer && { retailer: row.retailer }),
            ...(row.retailer_group && {
              retailer_group: row.retailer_group,
            }),
            ...(row.area && { area: row.area }),
            cycle: { id: scenariosSlice.selectedScenario.scenario.cycle_id },
            data_source_id: 1,
          },
        };

        const estimationsByCycleUrl = buildRequestUrl(
          {
            ...tupleRequestParams,
            group_by: ["price"],
            sum: ["units", "margin", "units_in", "sales"],
            avg_weight_by_units_in: ["price_in", "price_in_wo_taxes"],
            avg_weight_by_units: ["price_out_wo_taxes"],
          },
          "estimations_by_cycle"
        );

        const estimationsByCycleConfig = {
          method: "GET",
          url: estimationsByCycleUrl,
        };

        delete tupleRequestParams.filters.cycle;

        const [estimationsByCycleResponse] = await Promise.all([
          athenaClient(estimationsByCycleConfig),
        ]);

        // Rename keys before passing to groupByPriceAndReduceEstimations
        const renamedEstimations = estimationsByCycleResponse.data.map(
          (estimation) => ({
            ...estimation,
            price_in: estimation.avg_weight_by_units_in_price_in,
            price_in_wo_taxes:
              estimation.avg_weight_by_units_in_price_in_wo_taxes,
            price_out_wo_taxes:
              estimation.avg_weight_by_units_price_out_wo_taxes,
          })
        );

        const tempEstimations = groupByPriceAndReduceEstimations(
          renamedEstimations,
          metadataSlice["custom-projection"],
          "sum_"
        );
        setCurrentTupleData({
          estimations: tempEstimations,
        });
        setOwnElasticity(
          _.round(
            (
              await getElasticities({
                product_id: row.product_id,
                source_product_id: row.product_id,
                data_source_id: 1,
                is_product_client: 1,
                ...(row.retailer && { retailer_id: row.retailer_id }),
                ...(row.retailer_group && {
                  retailer_group_id: row.retailer_group.id,
                }),
                ...(row.area && { area_id: row.area_id }),
                avg_weight_by_pred_units_last_price: ["elasticity"],
                group_by: "product_id",
                filter_type: "same",
                min_date:
                  scenariosSlice.selectedScenario.scenario.cycle.initial_date,
                max_date:
                  scenariosSlice.selectedScenario.scenario.cycle.final_date,
              })
            ).data[0]?.avg_weight_by_pred_units_last_price_elasticity,
            2
          )
        );

        const filtersAux = {
          filters: {
            product: row.product,
            ...(row.retailer && { retailer: row.retailer }),
            ...(row.retailer_group && {
              retailer_group: row.retailer_group,
            }),
            ...(row.area && { area: row.area }),
          },
        };

        const scenarioEstimationsByDateUrl = buildRequestUrl(
          {
            ...filtersAux,
            scenario_id: scenariosSlice.selectedScenario.scenario_id,
          },
          "scenario_estimations_by_date"
        );
        const scenarioEstimationsByDateConfig = {
          method: "GET",
          url: scenarioEstimationsByDateUrl,
        };

        const scenarioEstimationsByDateResponse = await athenaClient(
          scenarioEstimationsByDateConfig
        );

        if (!isReference) {
          const scenarioEstimationsReferenceByDateUrl = buildRequestUrl(
            {
              ...filtersAux,
              scenario_id: scenariosSlice.referenceScenario.scenario_id,
            },
            "scenario_estimations_by_date"
          );
          const scenarioEstimationsReferenceByDateConfig = {
            method: "GET",
            url: scenarioEstimationsReferenceByDateUrl,
          };

          const scenarioEstimationsReferenceByDateResponse = await athenaClient(
            scenarioEstimationsReferenceByDateConfig
          );
          const scenarioEstimationsReferenceByDateWithExpandedProperties = _.keyBy(
            _.map(
              scenarioEstimationsReferenceByDateResponse.data,
              (scenarioEstimation) => {
                return {
                  ...scenarioEstimation,
                  price: _.round(
                    scenarioEstimation.price,
                    metadataSlice["custom-projection"].price[0].decimals
                  ),
                  product: catalogSlice.products[scenarioEstimation.product_id],
                  retailer: retailerSlice[scenarioEstimation.retailer_id],
                  area: areaSlice[scenarioEstimation.area_id],
                };
              }
            ),
            (estimation) =>
              `${estimation.product_id}-${estimation.area_id}-${estimation.retailer_id}-${estimation.date}`
          );

          dispatch(
            setReferenceScenario({
              estimationsByDate: {
                ...scenariosSlice.referenceScenario.estimationsByDate,
                ...scenarioEstimationsReferenceByDateWithExpandedProperties,
              },
            })
          );
        }

        const scenarioEstimationsByDateWithExpandedProperties = _.keyBy(
          _.map(
            scenarioEstimationsByDateResponse.data,
            (scenarioEstimation) => {
              return {
                ...scenarioEstimation,
                product: catalogSlice.products[scenarioEstimation.product_id],
                retailer: retailerSlice[scenarioEstimation.retailer_id],
                area: areaSlice[scenarioEstimation.area_id],
              };
            }
          ),
          (estimation) =>
            `${estimation.product_id}-${estimation.area_id}-${estimation.retailer_id}-${estimation.date}`
        );

        dispatch(
          setSelectedScenario({
            estimationsByDate: {
              ...scenariosSlice.selectedScenario.estimationsByDate,
              ...scenarioEstimationsByDateWithExpandedProperties,
            },
          })
        );
        handleCurrentRowPriceByMonth(
          scenarioEstimationsByDateWithExpandedProperties
        );

        let baseEstimation = _.find(estimationsByCycleResponse.data, {
          price: row.price,
        });
        if (baseEstimation === undefined) {
          // For when there is no price take the closest
          baseEstimation = estimationsByCycleResponse.data.reduce(
            (prev, curr) => {
              return Math.abs(curr.price - row.price) <
                Math.abs(prev.price - row.price)
                ? curr
                : prev;
            }
          );
        }

        setIsDataReady(true);
        setIsLoading(false);
      } catch (error) {
        setIsBadRequest(true);
      }
    }
  };
  const arrowColor = (condition, reference) => {
    return (100 * (condition - reference)) / reference > 0
      ? "#5AB793"
      : "#FD6464";
  };
  return (
    <div style={style}>
      <BadRequestDialog open={isBadRequest} />
      <div
        role="presentation"
        onClick={() => handleOnRowClick()}
        className={`${classes.row} ${index % 2 && classes.darkGreyBackground} ${
          rowValues.estimation_cycle_id === referenceRow.estimation_cycle_id &&
          !isReference &&
          !groupBy &&
          !isTradePromotionAnnual
            ? classes.light
            : null
        }`}
        style={{ cursor: isBlocked ? "default" : "pointer" }}
      >
        <div className={classes.rowCell}>
          <IconButton
            aria-label="expand row"
            size="small"
            classes={{
              root: classes.expandButton,
            }}
            disableRipple
            style={{ cursor: isBlocked ? "default" : "pointer" }}
          >
            {/* eslint-disable-next-line no-nested-ternary */}
            {isBlocked ? (
              <LockIcon fontSize="small" />
            ) : index === expandedRow ? (
              <KeyboardArrowUpIcon />
            ) : (
              <KeyboardArrowDownIcon />
            )}
          </IconButton>
        </div>
        {_.map(labelTableColumns, (columnName) => {
          if (groupBy && ["retailer", "area"].includes(columnName)) return null;
          return (
            <div
              className={`${classes.rowCell} ${classes.large}`}
              key={columnName}
            >
              <Tooltip
                title={rowValues[columnName].label}
                placement="top-start"
              >
                <Typography variant="body2" className={classes.cellValue}>
                  {rowValues[columnName].label}
                </Typography>
              </Tooltip>
            </div>
          );
        })}

        {_.map(numericTableColumns, (columnName) => {
          if (isReference && columnName === "investment_in") return null;
          return (
            metadataSlice["custom-projection"][columnName][0]
              .visibility_type === 2 && (
              <div
                key={columnName}
                className={`${classes.rowCell} ${classes.numeric}`}
              >
                <Typography variant="body2" className={classes.cellValue}>
                  {getFormattedCellValue(
                    columnName,
                    nFormatter(rowValues[columnName])
                  )}
                </Typography>
                {rowValues[columnName] !== null &&
                  Math.abs(
                    (100 * (rowValues[columnName] - referenceRow[columnName])) /
                      referenceRow[columnName]
                  )
                    .toFixed(2)
                    .toString() !== "0.00" &&
                  rowValues[columnName] !== null &&
                  rowValues[columnName] - referenceRow[columnName] !== 0 &&
                  !isReference && (
                    <Typography
                      variant="body2"
                      className={`${classes.cellPercentage}`}
                    >
                      <BsArrowUp
                        size={12}
                        strokeWidth={1.5}
                        style={{
                          marginRight: "5px",
                          color: `${
                            columnName === "price"
                              ? "#a2a2a2"
                              : arrowColor(
                                  rowValues[columnName],
                                  referenceRow[columnName]
                                )
                          }`,
                          transform: `rotateX(${
                            (100 *
                              (rowValues[columnName] -
                                referenceRow[columnName])) /
                              referenceRow[columnName] >
                            0
                              ? "0deg"
                              : "180deg"
                          })`,
                        }}
                      />
                      {rowValues[columnName] !== null &&
                        Math.abs(
                          (100 *
                            (rowValues[columnName] -
                              referenceRow[columnName])) /
                            referenceRow[columnName]
                        ).toFixed(2)}
                      %
                    </Typography>
                  )}
              </div>
            )
          );
        })}
      </div>
      <Collapse in={index === expandedRow} timeout={0} unmountOnExit>
        <CardComponent
          customStyle={classes.customCardStyle}
          title={
            <>
              <div className={classes.titleCardContainer}>
                <Typography variant="h6" className={classes.titleCard}>
                  Price Simulator - {titleCycleActive}
                </Typography>
                <div className={classes.textCardContainer}>
                  <Typography
                    variant="body2"
                    className={classes.textOwnElasticity}
                  >
                    Own Elasticity:
                  </Typography>
                  <Typography
                    variant="body2"
                    style={{
                      paddingLeft: "5px",
                    }}
                    className={classes.textOwnElasticity}
                  >
                    {ownElasticity}
                  </Typography>
                </div>
              </div>
              <div className={classes.selectorCycleContainer}>
                <SelectorCycleTradePromotionAnnualComponent
                  setTitleCycleActive={setTitleCycleActive}
                  setCurrentMonth={setCurrentMonth}
                  listCycles={listCycles}
                  isDataReady={isDataReady}
                />
              </div>
              <div
                className={
                  // eslint-disable-next-line no-nested-ternary
                  listCycles.length === 7 && isLargeScreen
                    ? classes.histogramChartContainerWithScrollSix
                    : // eslint-disable-next-line no-nested-ternary
                    listCycles.length === 7 &&
                      isTwoThousandScreen &&
                      isMediumScreen
                    ? classes.histogramChartContainerSpecificSix
                    : // eslint-disable-next-line no-nested-ternary
                    listCycles.length === 7 &&
                      isRangeDownScreen &&
                      isRangeUpScreen
                    ? classes.histogramChartContainerSmallSix
                    : // eslint-disable-next-line no-nested-ternary
                    listCycles.length === 7 && isMediumScreen
                    ? classes.histogramChartContainerMediumSix
                    : // eslint-disable-next-line no-nested-ternary
                    listCycles.length === 7 && isSmallScreen
                    ? classes.histogramChartContainerSmallSix
                    : // eslint-disable-next-line no-nested-ternary
                    listCycles.length === 7
                    ? classes.histogramChartContainerSix
                    : // eslint-disable-next-line no-nested-ternary
                    listCycles.length === 4 && isLargeScreen
                    ? classes.histogramChartContainerWithScrollThree
                    : // eslint-disable-next-line no-nested-ternary
                    listCycles.length === 4 &&
                      isTwoThousandScreen &&
                      isMediumScreen
                    ? classes.histogramChartContainerSpecificThree
                    : // eslint-disable-next-line no-nested-ternary
                    listCycles.length === 4 &&
                      isRangeDownScreen &&
                      isRangeUpScreen
                    ? classes.histogramChartContainerSmallThree
                    : // eslint-disable-next-line no-nested-ternary
                    listCycles.length === 4 && isMediumScreen
                    ? classes.histogramChartContainerMediumThree
                    : // eslint-disable-next-line no-nested-ternary
                    listCycles.length === 4 && isSmallScreen
                    ? classes.histogramChartContainerSmallThree
                    : // eslint-disable-next-line no-nested-ternary
                    listCycles.length === 4
                    ? classes.histogramChartContainerThree
                    : // eslint-disable-next-line no-nested-ternary
                    isLargeScreen
                    ? classes.histogramChartContainerWithScroll
                    : // eslint-disable-next-line no-nested-ternary
                    isTwoThousandScreen && isMediumScreen
                    ? classes.histogramChartContainerSpecific
                    : // eslint-disable-next-line no-nested-ternary
                    isRangeDownScreen && isRangeUpScreen
                    ? classes.histogramChartContainerSmall
                    : // eslint-disable-next-line no-nested-ternary
                    isMediumScreen
                    ? classes.histogramChartContainerMedium
                    : isSmallScreen
                    ? classes.histogramChartContainerSmall
                    : classes.histogramChartContainer
                }
              >
                <div
                  className={
                    // eslint-disable-next-line no-nested-ternary
                    listCycles.length === 7 && isLargeScreen
                      ? classes.textAvgPriceContainerWithScrollSix
                      : // eslint-disable-next-line no-nested-ternary
                      listCycles.length === 7 &&
                        isTwoThousandScreen &&
                        isMediumScreen
                      ? classes.textAvgPriceContainerSpecificSix
                      : // eslint-disable-next-line no-nested-ternary
                      listCycles.length === 7 &&
                        isRangeDownScreen &&
                        isRangeUpScreen
                      ? classes.textAvgPriceContainerSmallSix
                      : // eslint-disable-next-line no-nested-ternary
                      listCycles.length === 7 && isMediumScreen
                      ? classes.textAvgPriceContainerMediumSix
                      : // eslint-disable-next-line no-nested-ternary
                      listCycles.length === 7 && isSmallScreen
                      ? classes.textAvgPriceContainerSmallSix
                      : // eslint-disable-next-line no-nested-ternary
                      listCycles.length === 7
                      ? classes.textAvgPriceContainerSix
                      : // eslint-disable-next-line no-nested-ternary
                      listCycles.length === 4 && isLargeScreen
                      ? classes.textAvgPriceContainerWithScrollThree
                      : // eslint-disable-next-line no-nested-ternary
                      listCycles.length === 4 &&
                        isTwoThousandScreen &&
                        isMediumScreen
                      ? classes.textAvgPriceContainerSpecificThree
                      : // eslint-disable-next-line no-nested-ternary
                      listCycles.length === 4 &&
                        isRangeDownScreen &&
                        isRangeUpScreen
                      ? classes.textAvgPriceContainerSmallThree
                      : // eslint-disable-next-line no-nested-ternary
                      listCycles.length === 4 && isMediumScreen
                      ? classes.textAvgPriceContainerMediumThree
                      : // eslint-disable-next-line no-nested-ternary
                      listCycles.length === 4 && isSmallScreen
                      ? classes.textAvgPriceContainerSmallThree
                      : // eslint-disable-next-line no-nested-ternary
                      listCycles.length === 4
                      ? classes.textAvgPriceContainerThree
                      : // eslint-disable-next-line no-nested-ternary
                      isLargeScreen
                      ? classes.textAvgPriceContainerWithScroll
                      : // eslint-disable-next-line no-nested-ternary
                      isTwoThousandScreen && isMediumScreen
                      ? classes.textAvgPriceContainerSpecific
                      : // eslint-disable-next-line no-nested-ternary
                      isRangeDownScreen && isRangeUpScreen
                      ? classes.textAvgPriceContainerSpecificRange
                      : // eslint-disable-next-line no-nested-ternary
                      isMediumScreen
                      ? classes.textAvgPriceContainerMedium
                      : isSmallScreen
                      ? classes.textAvgPriceContainerSmall
                      : classes.textAvgPriceContainer
                  }
                >
                  <Typography variant="body2">Avg. Price</Typography>
                  <Typography
                    variant="body2"
                    className={classes.textAvgPriceNumber}
                  >
                    {rowValues.price}
                  </Typography>
                </div>
                <Grid
                  item
                  xs={6}
                  sm={6}
                  md={6}
                  lg={6}
                  className={classes.chartContainer}
                >
                  <HistogramChartComponent
                    currentRowPriceByDate={Object.values(currentRowPriceByDate)}
                    rowValueAvg={_.round(
                      rowValues.price,
                      metadataSlice["custom-projection"].price[0].decimals
                    )}
                    listCycles={_.filter(listCycles, (cycle) => {
                      return cycle !== "AllMonths";
                    })}
                  />
                </Grid>
                <div className={classes.textDescriptionHistogramContainer}>
                  <Typography
                    variant="body2"
                    className={classes.textDescriptionHistogram}
                  >
                    High
                  </Typography>
                  <Typography className={classes.textSmallSize}>Low</Typography>
                </div>
              </div>
            </>
          }
          body={
            <div
              style={{
                paddingBottom: 0,
                paddingTop: 0,
              }}
            >
              <Box className={classes.expansionDetailsContainer}>
                {isLoadingPriceChange && (
                  <div className={classes.customLoadingContainer}>
                    <ScaleLoader size={150} color={primaryColor} loading />
                  </div>
                )}
                {currentTupleData &&
                !isLoading &&
                isDataReady &&
                scenariosSlice.selectedScenario.estimationsByDate ? (
                  <div className={classes.itemContainer}>
                    <ElasticityCurveChartComponent
                      scenarioEstimations={
                        scenariosSlice.selectedScenario.estimations
                      }
                      isReference={isReference}
                      selectedRow={rowValues}
                      selectedRowByDate={selectedRowByDate}
                      selectedReferenceRowByDate={selectedReferenceRowByDate}
                      setIsMonthLevelChange={setIsMonthLevelChange}
                      referenceRow={referenceRow}
                      groupBy={groupBy}
                      setSelectedPrice={setSelectedPrice}
                      pricingMonthSelectedPrice={selectedPrice}
                      isTradePromotionAnnual={isTradePromotionAnnual}
                      tupleEstimations={currentTupleData?.estimations}
                    />
                  </div>
                ) : (
                  <ScaleLoader size={150} color={primaryColor} loading />
                )}
              </Box>

              <Typography variant="body1" className={classes.textSlider}>
                {priceOutLabelByTenant} (
                {metadataSlice["custom-projection"].price[0].unit})
              </Typography>
            </div>
          }
          estimationCardContainer
          isAnnualPricing
        />
      </Collapse>
    </div>
  );
}
